/**
 * Lifecycle of the post-signup template copy prompt.
 *
 * A user who signed up from a public template canvas has that template stored
 * server-side on the org's onboarding record (`template_prompt`). The first
 * home view spends it: the dialog offers the copy, and standard onboarding
 * stands aside for that one view. Provider-owned because the dialog (a portal
 * beside `HomeSurface`) and the recovery card (deep inside `HomeBrowser`) are
 * two views of the same state.
 *
 * Resolution (PATCH `template_prompt: null`) is terminal — taken, cleared, or
 * a dead source. Every other failure leaves the prompt pending so a reload
 * still offers it. Onboarding's own server state is never touched.
 */

import { useClearTemplatePrompt, useUserOnboarding } from "@/api/onboarding";
import { useUserInfo } from "@/api/user";
import * as analytics from "@/utils/analytics";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  clearTemplatePromptDismissal,
  isTemplatePromptDismissed,
  markTemplatePromptDismissed,
} from "./templatePromptDismissal";

export type TemplateCopyPrompt = {
  id: string;
  kind: "canvas" | "file";
  title?: string;
  authorName?: string;
};

export type TemplateCopyPromptView = "dialog" | "card" | "none";

type TemplateCopyPromptContextValue = {
  /** `null` once resolved, or when the record carries nothing usable. */
  prompt: TemplateCopyPrompt | null;
  view: TemplateCopyPromptView;
  /** Recovery card click — back to the dialog. */
  openDialog: () => void;
  /** "Maybe later" — client-side, no server write. */
  dismiss: () => void;
  /** Terminal: PATCH null, drop the dismissal, hide. */
  resolve: () => void;
  /** Latched suppression of the onboarding dialog for this home view. */
  deferOnboarding: boolean;
};

const TemplateCopyPromptContext =
  createContext<TemplateCopyPromptContextValue | null>(null);

/** `null` outside the provider — every consumer renders nothing there. */
export function useTemplateCopyPromptContext(): TemplateCopyPromptContextValue | null {
  return useContext(TemplateCopyPromptContext);
}

/** The suppress-once boolean on its own, for the onboarding dialog's gate. */
export function useTemplateCopyPromptDefer(): boolean {
  return useContext(TemplateCopyPromptContext)?.deferOnboarding ?? false;
}

export function TemplateCopyPromptProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: onboarding, isLoading } = useUserOnboarding({ enabled: true });
  const { data: userInfo } = useUserInfo();
  const clearTemplatePrompt = useClearTemplatePrompt();

  const orgId = userInfo?.organization?.id ?? "";

  // An unknown kind is treated as absent: never shown, never resolved — a
  // newer backend's prompt stays intact for the client that understands it.
  const prompt = useMemo((): TemplateCopyPrompt | null => {
    const stored = onboarding?.template_prompt;
    if (!stored?.id) return null;
    if (stored.kind !== "canvas" && stored.kind !== "file") return null;
    return {
      id: stored.id,
      kind: stored.kind,
      title: stored.title,
      authorName: stored.author_name,
    };
  }, [onboarding?.template_prompt]);

  const promptId = prompt?.id ?? "";

  // Read on every render, not latched in state: the first render after the
  // query lands is where `view` and `deferOnboarding` are decided, and a
  // post-render correction would arrive too late for both.
  const storedDismissed = useMemo(
    () => isTemplatePromptDismissed(orgId, promptId),
    [orgId, promptId],
  );
  // Keyed on the prompt so a new prompt starts undismissed without a reset.
  const [locallyDismissedId, setLocallyDismissedId] = useState<string | null>(
    null,
  );
  const dismissed = storedDismissed || locallyDismissedId === promptId;
  const [reopened, setReopened] = useState(false);
  /**
   * Bridges the PATCH round-trip, the same way `locallyDismissed` does in
   * `OnboardingWelcomeDialog` — the cached record still carries the prompt
   * until the mutation lands.
   */
  const [resolved, setResolved] = useState(false);

  const view: TemplateCopyPromptView =
    isLoading || !prompt || resolved
      ? "none"
      : dismissed && !reopened
        ? "card"
        : "dialog";

  const deferRef = useRef<boolean | null>(null);
  if (deferRef.current === null && !isLoading && onboarding) {
    // Latched, not derived: the view that showed the dialog keeps onboarding
    // suppressed after a copy or a dismiss, so nothing pops in behind the
    // user's own action. The next home mount re-evaluates from scratch.
    deferRef.current = view === "dialog";
  }

  const openDialog = useCallback(() => setReopened(true), []);

  const dismiss = useCallback(() => {
    if (!prompt) return;
    markTemplatePromptDismissed(orgId, prompt.id);
    setLocallyDismissedId(prompt.id);
    setReopened(false);
    analytics.trackTemplatePromptDismissed({
      kind: prompt.kind,
      id: prompt.id,
    });
  }, [orgId, prompt]);

  const resolvedRef = useRef(false);
  const resolve = useCallback(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    setResolved(true);
    clearTemplatePromptDismissal();
    clearTemplatePrompt();
  }, [clearTemplatePrompt]);

  const value = useMemo(
    () => ({
      prompt: view === "none" ? null : prompt,
      view,
      openDialog,
      dismiss,
      resolve,
      deferOnboarding: deferRef.current ?? false,
    }),
    [prompt, view, openDialog, dismiss, resolve],
  );

  return (
    <TemplateCopyPromptContext.Provider value={value}>
      {children}
    </TemplateCopyPromptContext.Provider>
  );
}
